import { Injectable } from '@angular/core';
import type { ItemsService } from '@app/@classes/abstract/items-service-interface';
import type { TablesOrViews, TablesOrViewsKeys } from '@app/@classes/supabase';
import { Observable, Subject, forkJoin } from 'rxjs';
import { debounceTime, share, switchMap, take } from 'rxjs/operators';

interface BatchRequest {
    service: ItemsService<any>;
    valueKey: string;
    values: Set<unknown>;
    subscribers: Array<{
        requestedValues: unknown[];
        subject: Subject<any[]>;
        requestKey: string; // Add request key for individual caching
    }>;
}

interface RequestCache {
    timestamp: number;
    data: any[]; // Store the actual data instead of the observable
}

@Injectable({
    providedIn: 'root',
})
export class SelectAsyncLoaderService {
    private pendingRequests = new Map<string, BatchRequest>();
    private requestTrigger$ = new Subject<void>();
    private requestCache = new Map<string, RequestCache>();

    // Maximum number of items per batch request
    private readonly BATCH_SIZE = 30;
    // Cache duration in milliseconds (30 second)
    private readonly CACHE_DURATION = 30000;

    constructor() {
        // Process batched requests with 300ms debounce (increased from 100ms to handle complex component initialization)
        this.requestTrigger$
            .pipe(
                debounceTime(300),
                switchMap(() => this.processPendingRequests()),
            )
            .subscribe();
    }

    /**
     * Load values for a select component, batching requests for the same service
     */
    loadValues<T extends TablesOrViews<TablesOrViewsKeys>>(
        service: ItemsService<T>,
        valueKey: keyof T,
        requestedValues: unknown[],
    ): Observable<T[]> {
        if (requestedValues.length === 0) {
            return new Observable(subscriber => {
                subscriber.next([]);
                subscriber.complete();
            });
        }

        // Create a unique key for this specific request (including the exact values)
        const requestKey = this.getRequestKey(service, valueKey as string, requestedValues);

        // Check if we have a recent cached request for exactly these values
        const cached = this.requestCache.get(requestKey);
        if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
            console.log(`🎯 [SelectAsyncLoader] ${service.TABLE} | Using cached request for "${requestKey}"`);
            return new Observable(subscriber => {
                subscriber.next(cached.data);
                subscriber.complete();
            });
        }

        // Create a unique key for this service + valueKey combination
        const batchKey = this.getBatchKey(service, valueKey as string);

        console.log(`🔄 [SelectAsyncLoader] ${service.TABLE} | Adding ${requestedValues.length} values to batch "${batchKey}"`);

        // Create subject for this specific request
        const subject = new Subject<T[]>();
        const observable = subject.asObservable().pipe(
            take(1),
            share()
        );

        // Don't cache until we have actual data - remove premature caching

        // Get or create batch request
        if (!this.pendingRequests.has(batchKey)) {
            this.pendingRequests.set(batchKey, {
                service,
                valueKey: valueKey as string,
                values: new Set(),
                subscribers: [],
            });
        }

        const batchRequest = this.pendingRequests.get(batchKey)!;

        // Add values to the batch
        for (const value of requestedValues) {
            batchRequest.values.add(value);
        }

        // Add subscriber to the batch with the request key for caching
        batchRequest.subscribers.push({
            requestedValues,
            subject,
            requestKey, // Add request key for individual caching
        });

        console.log(`📊 [SelectAsyncLoader] ${service.TABLE} | Batch "${batchKey}" now has ${batchRequest.values.size} values, ${batchRequest.subscribers.length} subscribers`);

        // Trigger the debounced request processing
        this.requestTrigger$.next();

        return observable;
    }

    private getRequestKey(service: ItemsService<any>, valueKey: string, values: unknown[]): string {
        // Create a unique key based on service, valueKey, and the specific values requested
        const sortedValues = [...values].sort().join(',');
        return `${service.TABLE}_${valueKey}_[${sortedValues}]`;
    }

    private getBatchKey(service: ItemsService<any>, valueKey: string): string {
        // Create a unique key based on service constructor name and valueKey
        return `${service.TABLE}_${valueKey}`;
    }

    private processPendingRequests(): Observable<void> {
        return new Observable(subscriber => {
            const requests = Array.from(this.pendingRequests.entries());
            this.pendingRequests.clear();

            if (requests.length === 0) {
                subscriber.next();
                subscriber.complete();
                return;
            }

            // Clean old cache entries
            this.cleanupCache();

            // Process each batch request
            let pendingCount = requests.length;

            for (const [batchKey, batchRequest] of requests) {
                this.executeBatchRequest(batchRequest).subscribe({
                    next: () => {
                        pendingCount--;
                        if (pendingCount === 0) {
                            subscriber.next();
                            subscriber.complete();
                        }
                    },
                    error: (error) => {
                        // Notify all subscribers of the error
                        for (const sub of batchRequest.subscribers) {
                            sub.subject.error(error);
                        }
                        pendingCount--;
                        if (pendingCount === 0) {
                            subscriber.next();
                            subscriber.complete();
                        }
                    }
                });
            }
        });
    }

    private cleanupCache(): void {
        const now = Date.now();
        for (const [key, cache] of this.requestCache.entries()) {
            if (now - cache.timestamp > this.CACHE_DURATION) {
                this.requestCache.delete(key);
            }
        }
    }

    private executeBatchRequest(batchRequest: BatchRequest): Observable<void> {
        return new Observable(subscriber => {
            const { service, valueKey, values, subscribers } = batchRequest;
            const valuesArray = Array.from(values);

            console.log(`🚀 [SelectAsyncLoader] ${service.TABLE} | Executing batch for ${service.constructor.name}.${valueKey}: ${valuesArray.length} values → ${subscribers.length} subscribers`);

            // Split values into chunks of BATCH_SIZE
            const chunks = this.chunkArray(valuesArray, this.BATCH_SIZE);

            if (chunks.length === 0) {
                // No chunks to process, distribute empty results
                for (const sub of subscribers) {
                    sub.subject.next([]);
                    sub.subject.complete();
                }
                subscriber.next();
                subscriber.complete();
                return;
            }

            // Create requests for each chunk
            const chunkRequests = chunks.map((chunk, index) => {
                const filter = {
                    [valueKey]: {
                        operator: 'in' as const,
                        column: valueKey,
                        active: true,
                        value: chunk,
                    },
                };

                return service.getMany({ filters: filter });
            });

            // Execute all chunk requests in parallel and combine results
            forkJoin(chunkRequests).subscribe({
                next: (chunkResults) => {
                    // Flatten all results from different chunks
                    const allItems = chunkResults.flat();

                    console.log(`✅ [SelectAsyncLoader] ${service.TABLE} | Batch completed: received ${allItems.length} items, distributing to ${subscribers.length} subscribers`);

                    // Distribute results to all subscribers
                    for (const sub of subscribers) {
                        const filteredItems = allItems.filter(item => {
                            const itemValue = item[valueKey as keyof typeof item];
                            const isIncluded = sub.requestedValues.includes(itemValue);

                            if (!isIncluded) {
                                // Try string conversion in case of type mismatch
                                const stringItemValue = String(itemValue);
                                const stringRequestedValues = sub.requestedValues.map(v => String(v));
                                const isIncludedAsString = stringRequestedValues.includes(stringItemValue);
                                return isIncludedAsString;
                            }

                            return true;
                        });

                        // Cache the individual result
                        if (sub.requestKey) {
                            this.requestCache.set(sub.requestKey, {
                                timestamp: Date.now(),
                                data: filteredItems
                            });
                        }

                        sub.subject.next(filteredItems);
                        sub.subject.complete();
                    }
                    subscriber.next();
                    subscriber.complete();
                },
                error: (error) => {
                    console.error(`❌ [SelectAsyncLoader] ${service.TABLE} | Batch failed:`, error);
                    // Notify all subscribers of the error
                    for (const sub of subscribers) {
                        sub.subject.error(error);
                    }
                    subscriber.error(error);
                }
            });
        });
    }

    /**
     * Split an array into chunks of specified size
     */
    private chunkArray<T>(array: T[], chunkSize: number): T[][] {
        const chunks: T[][] = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            chunks.push(array.slice(i, i + chunkSize));
        }
        return chunks;
    }
} 